import { createSlice, EntityState } from "@reduxjs/toolkit";
import { PartyInvolved } from "../../../api/PartiesInvolved";
import {
  deletePartyInvolvedThunk,
  fetchPartyOptionsThunk,
  upsertPartiesInvolvedThunk,
} from "./PartiesInvolvedActionsThunk";
import { partiesInvolvedAdapter } from "./PartiesInvolvedSelectors";
import { fetchCaseInfo } from "../../../thunks/Case";

enum EntityType {
  TENANT_CUSTOMERS = "TENANT_CUSTOMERS",
  TENANT_LOAN_STAKEHOLDERS = "TENANT_LOAN_STAKEHOLDERS",
  TENANT_EMPLOYEES = "TENANT_EMPLOYEES",
}

enum PartyTypes {
  BORROWER = "BORROWER",
  EMPLOYEE = "EMPLOYEE",
  FORMER_EMPLOYEE = "FORMER_EMPLOYEE",
  SOURCING_PARTNER = "SOURCING_PARTNER",
  SELLER = "SELLER",
  VENDOR = "VENDOR",
  OTHER = "OTHER",
}

export interface IPartyCorrelation {
  entity_type: EntityType;
  entity_id: string;
}

export interface IPartyOptions {
  party_type: PartyTypes;
  party_name: string;
  party_id?: string;
  correlation?: IPartyCorrelation;
}

export interface PartiesInvolvedState {
  parties: EntityState<PartyInvolved, string>; // Normalised state instead of `PartiesInvolvedResponse`
  partyOptions?: IPartyOptions[];
  status: "idle" | "loading" | "succeeded" | "failed";
  partyOptionsStatus?: "idle" | "loading" | "succeeded" | "failed";
  partyOptionsError?: string | null;
  partyOptionLoading?: boolean;
  error: string | null;
  caseId: string | null;
  partyOptionsCaseId?: string | null;
  upsertStatus?: "idle" | "loading" | "succeeded" | "failed";
  upsertError?: string | null;
  deleteStatus?: "idle" | "loading" | "succeeded" | "failed";
  deleteError?: string | null;
}

const initialState: PartiesInvolvedState = {
  parties: partiesInvolvedAdapter.getInitialState(),
  partyOptions: [] as IPartyOptions[],
  partyOptionsStatus: "idle",
  status: "idle",
  partyOptionLoading: false,
  partyOptionsError: null,
  error: null,
  caseId: null,
  partyOptionsCaseId: null,
};

const partiesInvolvedSlice = createSlice({
  name: "partiesInvolved",
  initialState: initialState,
  reducers: {
    clearPartiesInvolved: (state) => {
      partiesInvolvedAdapter.removeAll(state.parties);
      state.status = "idle";
      state.error = null;
    },
    clearPartyOptions: (state) => {
      state.partyOptions = [];
      state.partyOptionsStatus = "idle";
      state.partyOptionsError = null;
      state.partyOptionsCaseId = null;
    },
    resetUpsertStatus: (state) => {
      state.upsertStatus = "idle";
      state.upsertError = null;
    },
    resetDeleteStatus: (state) => {
      state.deleteStatus = "idle";
      state.deleteError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCaseInfo.pending, (state, action) => {
        const incomingCaseId = action.meta.arg.case_id;

        if (state.caseId !== incomingCaseId) {
          partiesInvolvedAdapter.removeAll(state.parties);
        }

        state.caseId = incomingCaseId;
        state.status = "loading";
        state.error = null;
        partiesInvolvedAdapter.removeAll(state.parties);
      })
      .addCase(fetchCaseInfo.rejected, (state, action) => {
        state.caseId = action.meta.arg.case_id;
        state.status = "failed";
        state.error = "Failed to fetch Parties Involved";
      })
      .addCase(fetchCaseInfo.fulfilled, (state, action) => {
        const caseData = action.payload.cases?.[0];

        state.caseId = caseData?.case_id ?? action.meta.arg.case_id;
        state.status = "succeeded";
        state.error = null;

        partiesInvolvedAdapter.setAll(state.parties, caseData?.parties_involved ?? []);
      })      
      .addCase(upsertPartiesInvolvedThunk.pending, (state) => {
        state.upsertStatus = "loading";
        state.upsertError = null;
      })
      .addCase(upsertPartiesInvolvedThunk.rejected, (state, action) => {
        state.upsertStatus = "failed";
        state.upsertError =
          action.payload || "Failed to upsert parties involved";
      })
      .addCase(upsertPartiesInvolvedThunk.fulfilled, (state, action) => {
        state.upsertStatus = "succeeded";
        state.upsertError = null;

        const updatedParties = action.meta.arg.request.parties_involved;

        updatedParties.forEach((partyUpdate) => {
          const existingParty = state.parties.entities[partyUpdate.party_id];
          const mergedParty = {
            ...existingParty,
            ...partyUpdate,
          };
          partiesInvolvedAdapter.upsertOne(state.parties, mergedParty as PartyInvolved);
        });
      })
      .addCase(deletePartyInvolvedThunk.pending, (state) => {
        state.deleteStatus = "loading";
        state.deleteError = null;
      })
      .addCase(deletePartyInvolvedThunk.rejected, (state, action) => {
        state.deleteStatus = "failed";
        state.deleteError = action.payload || "Failed to delete party involved";
      })
      .addCase(deletePartyInvolvedThunk.fulfilled, (state, action) => {
        state.deleteStatus = "succeeded";
        state.deleteError = null;
        partiesInvolvedAdapter.removeOne(
          state.parties,
          action.meta.arg.party_id
        );
      })
      .addCase(fetchPartyOptionsThunk.pending, (state) => {
        state.partyOptionsStatus = "loading";
        state.partyOptionsError = null;
      })
      .addCase(fetchPartyOptionsThunk.rejected, (state, action) => {
        state.partyOptionsStatus = "failed";
        state.partyOptionsError =
          action.payload || "Failed to fetch party options";
      })
      .addCase(fetchPartyOptionsThunk.fulfilled, (state, action) => {
        state.partyOptionsStatus = "succeeded";
        state.partyOptions = action.payload.party_options;
        state.partyOptionsCaseId = action.meta.arg;
      });
  },
});

export const clearPartiesInvolved =
  partiesInvolvedSlice.actions.clearPartiesInvolved;
export const clearPartyOptions = partiesInvolvedSlice.actions.clearPartyOptions;
export const resetUpsertStatus = partiesInvolvedSlice.actions.resetUpsertStatus;
export const resetDeleteStatus = partiesInvolvedSlice.actions.resetDeleteStatus;
export const partiesInvolvedReducer = partiesInvolvedSlice.reducer;
